/* eslint-disable @typescript-eslint/no-explicit-any */

import { injectable } from 'inversify';
import Dexie from 'dexie';
import { Identity } from 'openfin/_v2/main';

import { SettingsMap, StoredSetting } from '../StoredSetting';
import { StoredNotification } from '../StoredNotification';
import { StoredApplication } from '../Environment';
import { AsyncInit } from '../../controller/AsyncInit';
import { DatabaseError } from '../Errors';
import { PartiallyWritable } from '../../utils/types';
import { getMarkdownTextContent } from '../../view/utils/Markdown';
import {
  NotificationOptions,
  TemplateMarkdown,
  TemplateNames,
} from '../../../client/templates/templates';
import { NotificationInternal } from '../../../client/internal';
import { ButtonOptions } from '../../../client';
import { StoredStream } from '../StoredStream';

import { Collection } from './Collection';
import { IndicatorColor, IndicatorType, NotificationIndicator } from '../../../client/indicator';

export const enum CollectionMap {
  NOTIFICATIONS = 'notifications',
  SETTINGS = 'settings',
  APPLICATIONS = 'applications',
  STREAMS = 'streams',
}

export interface Collections {
  [CollectionMap.NOTIFICATIONS]: StoredNotification;
  [CollectionMap.SETTINGS]: StoredSetting;
  [CollectionMap.APPLICATIONS]: StoredApplication;
  [CollectionMap.STREAMS]: StoredStream;
}

@injectable()
export class Database extends AsyncInit {
  private readonly _database: Dexie;
  private readonly _collections: Map<CollectionMap, Collection<any>>;

  constructor() {
    super();
    this._database = new Dexie('notifications-service');
    this._collections = new Map<CollectionMap, Collection<any>>();

    // These database versions should never change. Instead, create a new version with a migration method if necessary
    this._database.version(1).stores({
      [CollectionMap.NOTIFICATIONS]: '&id',
      [CollectionMap.SETTINGS]: '&id',
    });

    this._database
      .version(2)
      .stores({
        [CollectionMap.APPLICATIONS]: '&id',
      })
      .upgrade(async (transaction: Dexie.Transaction) => {
        console.groupCollapsed('Migrating to database version 2');

        const typedTransaction = transaction as Dexie.Transaction & {
          [CollectionMap.NOTIFICATIONS]: Dexie.Table<StoredNotification, string>;
          [CollectionMap.APPLICATIONS]: Dexie.Table<StoredApplication, string>;
        };
        const notificationsCollection = typedTransaction[
          CollectionMap.NOTIFICATIONS
        ].toCollection();
        const applicationsCollection = typedTransaction[CollectionMap.APPLICATIONS].toCollection();

        await notificationsCollection.modify((notification: StoredNotification) => {
          // Notifications created before the expiration feature will have undefined "expiry", so we manually set it to null
          if (
            typeof notification.notification.expires !== 'number' &&
            notification.notification.expires !== null
          ) {
            console.log(`Setting "expires" to null on notification ${notification.id}`);

            const note = notification.notification as PartiallyWritable<
              NotificationInternal,
              'expires'
            >;
            note.expires = null;
          }

          // Notifications created before support of "onClose" and "onExpire" will have them undefined, so we manually set them to null
          if (!notification.notification.onClose && notification.notification.onClose !== null) {
            console.log(`Setting "onClose" to null on notification ${notification.id}`);

            const note = notification.notification as PartiallyWritable<
              NotificationInternal,
              'onClose'
            >;
            note.onClose = null;
          }
          if (!notification.notification.onExpire && notification.notification.onExpire !== null) {
            console.log(`Setting "onExpire" to null on notification ${notification.id}`);

            const note = notification.notification as PartiallyWritable<
              NotificationInternal,
              'onExpire'
            >;
            note.onExpire = null;
          }
        });

        await applicationsCollection.modify((application: StoredApplication) => {
          // StoredApplication may have been created without a title, so copy from ID
          if (application.title === undefined) {
            console.log(`Setting "title" on application ${application.id}`);

            const app = application as PartiallyWritable<StoredApplication, 'title'>;
            app.title = app.id;
          }
        });

        console.groupEnd();
      });

    this._database.version(3).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 3');

      const notifications = transaction.table<StoredNotification, string>(
        CollectionMap.NOTIFICATIONS
      );
      const notificationsCollection = notifications.toCollection();

      await notificationsCollection.modify(
        (notification: PartiallyWritable<StoredNotification, 'source'>) => {
          // Convert 'source' from Identity to NotificationSource
          notification.source = {
            type: 'desktop',
            identity: (notification.source as unknown) as Identity,
          };
        }
      );

      console.groupEnd();
    });

    this._database.version(4).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 4');

      const notifications = transaction.table<StoredNotification, string>(
        CollectionMap.NOTIFICATIONS
      );
      const notificationsCollection = notifications.toCollection();

      await notificationsCollection.modify((notification: StoredNotification) => {
        // Notifications created before the sticky feature will
        // have an undefined “sticky” key, so we manually set it to “transient”.
        if (!notification.notification.sticky) {
          console.log(`Setting "sticky" to 'transient' on notification ${notification.id}`);

          const note = notification.notification as PartiallyWritable<
            NotificationInternal,
            'sticky'
          >;
          note.sticky = 'transient';
        }
      });

      console.groupEnd();
    });

    this._database.version(5).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 5');

      const settings = transaction.table<StoredSetting, string>(CollectionMap.SETTINGS);
      const settingsArray = await settings.toArray();
      const swapTable = [
        SettingsMap.CenterLocked,
        SettingsMap.CenterMuted,
        SettingsMap.CenterPosition,
      ];

      // Primary key update will fail in upgrade, clean and rebuild instead.
      settings.clear();
      settingsArray.forEach((setting) => {
        settings.add({ id: swapTable[(setting.id as any) as number], value: setting.value });
        console.log(
          `Updated setting id from ${setting.id} to ${swapTable[(setting.id as any) as number]}`
        );
      });

      console.groupEnd();
    });

    this._database.version(6).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 6');

      const notifications = transaction.table<StoredNotification, string>(
        CollectionMap.NOTIFICATIONS
      );
      const notificationsCollection = notifications.toCollection();

      await notificationsCollection.modify((notification: StoredNotification) => {
        // Previous notifications do not have bodyText generated from the body markdown
        if (!notification.bodyText) {
          console.log(`Generating bodyText for ${notification.id}`);

          const note = notification as PartiallyWritable<
            StoredNotification<TemplateMarkdown>,
            'bodyText'
          >;

          note.bodyText = getMarkdownTextContent(note.notification.body);
        }
      });

      console.groupEnd();
    });

    this._database.version(7).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 7');

      const notifications = transaction.table<StoredNotification, string>(
        CollectionMap.NOTIFICATIONS
      );
      const notificationsCollection = notifications.toCollection();

      await notificationsCollection.modify((notification: StoredNotification) => {
        if (
          notification.notification.buttons !== undefined &&
          notification.notification.buttons.length > 0
        ) {
          console.log(`Setting indices of buttons on notification ${notification.id}`);
          notification.notification.buttons.forEach((button, index) => {
            if (!button.index) {
              const indexedButton = button as PartiallyWritable<ButtonOptions, 'index'>;
              indexedButton.index = index;
            }
          });
        }
      });

      console.groupEnd();
    });

    // Add `template: 'markdown'` property to `notification`
    this._database.version(8).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 8');

      const notifications = transaction.table<StoredNotification, string>(
        CollectionMap.NOTIFICATIONS
      );
      const notificationsCollection = notifications.toCollection();

      await notificationsCollection.modify((storedNotification: StoredNotification) => {
        if (!storedNotification.notification.template) {
          (storedNotification.notification as PartiallyWritable<
            NotificationOptions,
            'template'
          >).template = TemplateNames.markdown;
        }
      });

      console.groupEnd();
    });

    this._database
      .version(9)
      .stores({
        [CollectionMap.STREAMS]: '&id',
      })
      .upgrade(() => {
        console.groupCollapsed('Migrating to database version 9');
      });

    this._database.version(10).upgrade(async (transaction: Dexie.Transaction) => {
      console.groupCollapsed('Migrating to database version 10');

      const notifications = transaction.table<StoredNotification, string>(
        CollectionMap.NOTIFICATIONS
      );

      const notificationsCollection = notifications.toCollection();

      await notificationsCollection.modify((storedNotification: StoredNotification) => {
        if (!storedNotification.notification.priority) {
          if (storedNotification.notification.indicator) {
            switch (storedNotification.notification.indicator.type) {
              case IndicatorType.FAILURE:
                (storedNotification.notification as PartiallyWritable<
                  NotificationInternal,
                  'priority'
                >).priority = 4;
                break;
              case IndicatorType.WARNING:
                (storedNotification.notification as PartiallyWritable<
                  NotificationInternal,
                  'priority'
                >).priority = 3;
                break;
              case IndicatorType.SUCCESS:
                (storedNotification.notification as PartiallyWritable<
                  NotificationInternal,
                  'priority'
                >).priority = 2;
                break;
            }
          } else {
            (storedNotification.notification as PartiallyWritable<
              NotificationInternal,
              'priority'
            >).priority = 1;
          }
        }

        if (
          storedNotification.notification.indicator &&
          !storedNotification.notification.indicator.color
        ) {
          switch (storedNotification.notification.indicator.type) {
            case IndicatorType.FAILURE:
              (storedNotification.notification.indicator as PartiallyWritable<
                NotificationIndicator,
                'color'
              >).color = IndicatorColor.RED;
              break;
            case IndicatorType.WARNING:
              (storedNotification.notification.indicator as PartiallyWritable<
                NotificationIndicator,
                'color'
              >).color = IndicatorColor.YELLOW;
              break;
            case IndicatorType.SUCCESS:
              (storedNotification.notification.indicator as PartiallyWritable<
                NotificationIndicator,
                'color'
              >).color = IndicatorColor.GREEN;
              break;
          }
        }
      });

      console.groupEnd();
    });

    this.createCollections(this._database.tables);
  }

  protected async init(): Promise<void> {
    try {
      await this._database.open();
    } catch (e) {
      // Version 1 database may or may not have the Applications store, so we need to handle both cases
      if (e instanceof Dexie.DexieError) {
        console.warn('Failed to open database, reattempting with alternative version 1 schema');

        this._database.version(1).stores({
          [CollectionMap.NOTIFICATIONS]: '&id',
          [CollectionMap.SETTINGS]: '&id',
          [CollectionMap.APPLICATIONS]: '&id',
          [CollectionMap.STREAMS]: '&id',
        });

        await this._database.open();
      } else {
        throw e;
      }
    }
  }

  /**
   * Returns a collection of the provided name.
   * @param collectionName The collection name.
   */
  public get<T extends keyof Collections>(collectionName: T): Collection<Collections[T]> {
    const table = this._collections.get(collectionName);

    if (table) {
      return table;
    } else {
      throw new DatabaseError(`Table with id ${collectionName} not found.`);
    }
  }

  private createCollections(tables: Dexie.Table<Collections[keyof Collections], string>[]): void {
    tables.forEach((table) => {
      this._collections.set(table.name as CollectionMap, new Collection(table));
    });
  }
}
